// src/components/legal/legalDocuments.js
// Copyright (c) 2025 Nebularis Holdings LLC
// See IP License Agreement for usage restrictions.
// Customer-facing legal and policy content authority for OASYS Privacy Policy, Customer Terms, and Data Lifecycle Policy.

export const LEGAL_DOCUMENTS = {
  "privacy": {
    "key": "privacy",
    "routeMode": "privacy",
    "title": "OASYS Privacy Policy",
    "shortTitle": "Privacy Policy",
    "eyebrow": "OASYS Legal",
    "description": "How OASYS collects, uses, discloses, protects, and otherwise processes personal information in connection with the Service.",
    "effectiveDate": "September 1, 2026",
    "lastUpdated": "September 1, 2026",
    "version": "1.0",
    "intro": [
      "Budd Hub LLC, doing business as OASYS Solutions (“OASYS,” “we,” “us,” or “our”), operates the OASYS software-as-a-service platform and related websites, applications, services, support functions, and enterprise compliance capabilities (collectively, the “Service”).",
      "This Privacy Policy explains how we collect, use, disclose, protect, and otherwise process personal information in connection with the Service.",
      "OASYS is designed primarily for organizations and their authorized business users. In many circumstances, an organization using OASYS determines what information is submitted to or processed through the Service. In those circumstances, OASYS processes that information on behalf of the customer organization and subject to the applicable customer agreement, instructions, and law."
    ],
    "sections": [
      {
        "title": "1. Scope of this Privacy Policy",
        "blocks": [
          {
            "type": "p",
            "text": "This Privacy Policy applies to personal information that OASYS processes in connection with OASYS accounts and authorized users; customer and prospective-customer relationships; use of the OASYS application and related services; customer-authorized integrations and connectors; customer support and business communications; OASYS Intelligence and other AI-assisted functionality; security, diagnostic, operational, and audit activities; and OASYS websites or other online properties that link to this Privacy Policy."
          },
          {
            "type": "p",
            "text": "This Privacy Policy does not govern the independent privacy practices of third-party services, cloud providers, identity providers, or other systems that a customer chooses to connect to OASYS."
          }
        ]
      },
      {
        "title": "2. Our Role and Customer Data",
        "blocks": [
          {
            "type": "h3",
            "text": "Information OASYS processes for its own business purposes"
          },
          {
            "type": "p",
            "text": "For information such as account administration, business contacts, security records, product usage information, support communications, and our direct commercial relationship with an organization, OASYS may determine the purposes and means of processing."
          },
          {
            "type": "h3",
            "text": "Customer Data"
          },
          {
            "type": "p",
            "text": "Organizations may submit, connect, generate, or otherwise make information available to OASYS in connection with their use of the Service (“Customer Data”)."
          },
          {
            "type": "p",
            "text": "For Customer Data that OASYS processes on behalf of a customer organization, the customer generally determines the purposes for which the information is processed and OASYS acts as a processor, service provider, contractor, or similar role as those terms are defined by applicable law."
          },
          {
            "type": "p",
            "text": "Customers retain their rights in Customer Data. OASYS does not acquire ownership of Customer Data merely because the data is processed through the Service."
          },
          {
            "type": "p",
            "text": "If you are an individual whose information is contained in an OASYS customer environment, your organization may be the appropriate party to contact regarding access, correction, deletion, or other requests relating to that Customer Data."
          }
        ]
      },
      {
        "title": "3. Information We Collect",
        "blocks": [
          {
            "type": "h3",
            "text": "Account and identity information"
          },
          {
            "type": "p",
            "text": "This may include name, business email address, account identifiers, organization membership, role and permission information, authentication-related identifiers, invitation status, login activity, and administrative access information. Authentication credentials may be handled through identity and authentication providers used by OASYS."
          },
          {
            "type": "h3",
            "text": "Organization and business information"
          },
          {
            "type": "p",
            "text": "Customers may provide organization name, business contacts, industry, operational profile, compliance objectives, selected frameworks, systems and technology environment, administrative preferences, and organizational roles and responsibilities."
          },
          {
            "type": "h3",
            "text": "Compliance and operational information"
          },
          {
            "type": "p",
            "text": "Depending on customer use, OASYS may process controls and control status, findings, risks, evidence, remediation activities, workflows, validation records, policies and procedures, SOPs, reports, audit records, approved exceptions, governance decisions, and operational or compliance history."
          },
          {
            "type": "h3",
            "text": "Connector and integration information"
          },
          {
            "type": "p",
            "text": "When a customer authorizes a connection between OASYS and another system, we may process provider and account identifiers, authorization metadata, connector configuration, requested or granted scopes, synchronization status, health and verification information, evidence and metadata retrieved through the authorized connection, and connection or synchronization history."
          },
          {
            "type": "p",
            "text": "OASYS connectors are designed around customer-authorized, read-only evidence collection. Customers determine which supported systems they choose to connect."
          },
          {
            "type": "p",
            "text": "Durable connector authorization material is encrypted before storage and decrypted only when required for authorized execution."
          },
          {
            "type": "h3",
            "text": "Customer-submitted content"
          },
          {
            "type": "p",
            "text": "Users may submit text, files, documents, screenshots, records, instructions, notes, evidence, or other content to the Service. Customers are responsible for ensuring that they have authority to provide such information to OASYS and for limiting submissions to information reasonably necessary for their use of the Service."
          },
          {
            "type": "h3",
            "text": "OASYS Intelligence and AI interaction information"
          },
          {
            "type": "p",
            "text": "When customers use OASYS Intelligence or other AI-assisted capabilities, we may process user questions or instructions, selected OASYS context, relevant OASYS records and identifiers, generated responses, model and interaction metadata, token and usage information, and audit records relating to the interaction."
          },
          {
            "type": "p",
            "text": "OASYS Intelligence is designed to operate on governed OASYS context. AI-generated content is advisory. Deterministic OASYS records, calculations, validation engines, authorization state, workflows, and other governed application state remain authoritative."
          },
          {
            "type": "p",
            "text": "AI functionality does not independently grant permissions, approve risk, execute remediation, validate a correction, close a task, or override customer or OASYS authorization controls."
          },
          {
            "type": "h3",
            "text": "Usage, device, security, and diagnostic information"
          },
          {
            "type": "p",
            "text": "We may automatically collect browser and device information, IP address, timestamps, application routes and feature usage, request and correlation identifiers, authentication and security events, error and diagnostic information, service health information, performance and latency information, and system or audit logs."
          },
          {
            "type": "h3",
            "text": "Communications"
          },
          {
            "type": "p",
            "text": "If you contact OASYS, we may process your name and contact details, company information, correspondence, support requests, security reports, sales inquiries, legal or privacy requests, and related records necessary to respond."
          },
          {
            "type": "h3",
            "text": "Commercial and billing information"
          },
          {
            "type": "p",
            "text": "We may process information relating to customer contracts, plans, invoices, billing contacts, transaction status, and subscription or commercial account administration. If payment-card functionality is made available through a payment provider, payment credentials may be collected directly by that provider rather than stored directly by OASYS."
          }
        ]
      },
      {
        "title": "4. How We Obtain Information",
        "blocks": [
          {
            "type": "p",
            "text": "We may receive information directly from users; from customer organizations and administrators; through customer-authorized connectors; automatically through use of the Service; from authentication and infrastructure providers; through support, sales, legal, or security communications; from service providers acting on our behalf; and from public or business sources when appropriate for legitimate business purposes."
          }
        ]
      },
      {
        "title": "5. How We Use Information",
        "blocks": [
          {
            "type": "p",
            "text": "We may use personal information to:"
          },
          {
            "type": "ul",
            "items": [
              "provide and operate OASYS;",
              "authenticate users and manage accounts;",
              "maintain tenant and authorization boundaries;",
              "process customer-authorized connector activity;",
              "provide compliance, evidence, workflow, validation, reporting, and governance functionality;",
              "provide OASYS Intelligence and other requested features;",
              "maintain audit trails and application history;",
              "communicate with customers and users and deliver support;",
              "manage customer relationships and contracts;",
              "detect and prevent fraud, abuse, unauthorized activity, and security incidents;",
              "monitor service availability and performance and diagnose technical issues;",
              "enforce customer agreements and acceptable-use requirements;",
              "comply with legal obligations and establish, exercise, or defend legal claims; and",
              "improve the reliability, usability, and security of the Service."
            ]
          },
          {
            "type": "p",
            "text": "Where applicable law requires a legal basis for processing, our bases may include performing a contract, complying with legal obligations, pursuing legitimate business and security interests, protecting rights and safety, and obtaining consent where required."
          }
        ]
      },
      {
        "title": "6. Customer-Authorized Connectors",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS supports integrations with third-party systems selected by customers. Examples may include cloud, identity, source-control, productivity, security, and infrastructure platforms."
          },
          {
            "type": "p",
            "text": "OASYS will process information accessible through a connector only in connection with the authorization, configuration, and functionality associated with that connection."
          },
          {
            "type": "p",
            "text": "Customers are responsible for determining whether to authorize a connector, maintaining appropriate authority to connect the third-party system, selecting appropriate accounts and permissions, and ensuring their use complies with agreements governing the connected system."
          },
          {
            "type": "p",
            "text": "Third-party providers remain responsible for their own privacy and security practices."
          }
        ]
      },
      {
        "title": "7. OASYS Intelligence and Third-Party AI Services",
        "blocks": [
          {
            "type": "p",
            "text": "Certain OASYS features use artificial intelligence services to provide grounded explanations, summaries, recommendations, and other advisory functionality."
          },
          {
            "type": "p",
            "text": "When such functionality is invoked, OASYS may send relevant governed context to an AI service provider, such as OpenAI, as necessary to provide the requested functionality."
          },
          {
            "type": "p",
            "text": "OASYS applies product controls intended to limit AI context to information relevant to the requested task and to preserve deterministic OASYS systems as the authoritative source of operational state."
          },
          {
            "type": "p",
            "text": "AI outputs may be incomplete or incorrect and should be reviewed by appropriate personnel. Customers remain responsible for decisions based on AI-assisted information."
          },
          {
            "type": "p",
            "text": "OASYS may maintain audit and usage records associated with AI interactions for security, governance, troubleshooting, product operation, and accountability."
          }
        ]
      },
      {
        "title": "8. How We Disclose Information",
        "blocks": [
          {
            "type": "h3",
            "text": "Service providers and subprocessors"
          },
          {
            "type": "p",
            "text": "We use service providers that support functions such as cloud hosting and infrastructure, databases and storage, authentication, email delivery, monitoring and security, AI processing, customer support, billing and payment processing if applicable, and other technical or professional services. These providers may process information on our behalf subject to applicable contractual and security requirements."
          },
          {
            "type": "h3",
            "text": "Customer administrators and authorized users"
          },
          {
            "type": "p",
            "text": "Information within a customer environment may be available to customer administrators and other users according to the organization’s permissions, roles, and configuration."
          },
          {
            "type": "h3",
            "text": "Legal and safety disclosures"
          },
          {
            "type": "p",
            "text": "We may disclose information when reasonably necessary to comply with law, regulation, legal process, or governmental request; protect the rights, property, security, or safety of OASYS, our customers, users, or others; investigate fraud, misuse, or security incidents; enforce agreements; or establish or defend legal claims."
          },
          {
            "type": "h3",
            "text": "Business transactions"
          },
          {
            "type": "p",
            "text": "If OASYS or relevant business assets are involved in a merger, acquisition, financing, restructuring, sale, or similar transaction, information may be disclosed as part of that transaction subject to applicable law and appropriate safeguards."
          }
        ]
      },
      {
        "title": "9. Sale, Sharing, and Advertising",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS is an enterprise software service and is not designed as an advertising-supported consumer platform."
          },
          {
            "type": "p",
            "text": "We do not disclose Customer Data to third parties for those third parties to independently use for advertising merely because that data is processed through OASYS."
          },
          {
            "type": "p",
            "text": "Where applicable privacy law defines particular activities as a “sale,” “sharing,” or use for targeted advertising, OASYS will provide notices and choices required by applicable law if and when those practices apply."
          }
        ]
      },
      {
        "title": "10. Security",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS uses administrative, technical, and organizational measures designed to protect information against unauthorized access, use, alteration, disclosure, or destruction."
          },
          {
            "type": "p",
            "text": "These measures include controls relating to identity and authentication, authorization, tenant isolation, protected production environments, encrypted production network communications, encryption of durable connector authorization material before storage, logging and auditability, secrets management, monitoring and incident detection, and controlled deployment and operational practices."
          },
          {
            "type": "p",
            "text": "No method of electronic transmission, processing, or storage is completely secure. Accordingly, we cannot guarantee absolute security."
          },
          {
            "type": "p",
            "text": "Security concerns or suspected vulnerabilities may be reported to security@oasyshq.com."
          }
        ]
      },
      {
        "title": "11. Data Retention",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS retains information for periods reasonably necessary to provide the Service; maintain customer and account relationships; satisfy contractual requirements; maintain security and auditability; resolve disputes; enforce agreements; and comply with legal, regulatory, accounting, or other obligations."
          },
          {
            "type": "p",
            "text": "Retention may differ based on the category of information, customer agreement, purpose of processing, security requirements, and applicable law."
          },
          {
            "type": "p",
            "text": "Some records may need to be preserved for audit, security, legal, contractual, or compliance purposes after other customer information becomes eligible for deletion."
          },
          {
            "type": "p",
            "text": "Information contained in backups may remain until the applicable backup expires or is securely replaced through normal backup lifecycle processes."
          },
          {
            "type": "p",
            "text": "OASYS does not represent that one universal retention period applies to every category of information. Additional data-lifecycle and offboarding practices may be described in customer agreements, documentation, or applicable OASYS policies."
          }
        ]
      },
      {
        "title": "12. Account Closure, Export, and Deletion",
        "blocks": [
          {
            "type": "p",
            "text": "Customers may request assistance with account-related export, offboarding, or deletion processes where applicable."
          },
          {
            "type": "p",
            "text": "Requests are subject to the applicable customer agreement, administrative authorization, technical feasibility, security requirements, legal or regulatory obligations, required preservation of certain audit or other records, and backup lifecycle limitations."
          },
          {
            "type": "p",
            "text": "OASYS does not silently treat account closure as authorization to immediately destroy all customer records."
          },
          {
            "type": "p",
            "text": "Authorized requests concerning OASYS-controlled personal information may be submitted to legal@oasyshq.com. Where OASYS processes personal information solely on behalf of a customer organization, we may direct the individual to that organization or coordinate with the organization as appropriate."
          }
        ]
      },
      {
        "title": "13. Privacy Rights",
        "blocks": [
          {
            "type": "p",
            "text": "Depending on your location and applicable law, you may have rights concerning personal information, which may include rights to request access, obtain information regarding collection or disclosure, request correction or deletion, obtain a portable copy of certain information, opt out of certain processing where legally applicable, withdraw consent where processing relies on consent, appeal certain decisions, and receive equal treatment for exercising applicable privacy rights."
          },
          {
            "type": "p",
            "text": "These rights are subject to applicable legal exceptions and verification requirements."
          },
          {
            "type": "p",
            "text": "To submit a privacy request regarding information controlled by OASYS, contact legal@oasyshq.com and provide sufficient information for us to reasonably identify the relevant account or relationship. We may need to verify your identity and authority before fulfilling certain requests."
          },
          {
            "type": "p",
            "text": "If your information is processed through OASYS on behalf of your employer or another customer organization, please contact that organization first where appropriate. OASYS may assist the customer in fulfilling applicable requests."
          },
          {
            "type": "p",
            "text": "We will not unlawfully discriminate against an individual for exercising applicable privacy rights."
          }
        ]
      },
      {
        "title": "14. California and Other U.S. State Privacy Rights",
        "blocks": [
          {
            "type": "p",
            "text": "Residents of certain U.S. states may have additional privacy rights under applicable state law. Where such laws apply to OASYS, we will process qualifying requests in accordance with applicable requirements, including rights relating to access, correction, deletion, portability, and applicable opt-out rights."
          },
          {
            "type": "p",
            "text": "California residents may have additional rights under the California Consumer Privacy Act, as amended, including rights concerning access, correction, deletion, and certain sale or sharing practices where applicable."
          },
          {
            "type": "p",
            "text": "Nothing in this section is intended to represent that a particular privacy statute applies to OASYS or a particular processing activity when its statutory applicability requirements are not satisfied."
          }
        ]
      },
      {
        "title": "15. International Processing",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS is operated from the United States. Information may be processed in the United States or other countries where OASYS or its service providers operate."
          },
          {
            "type": "p",
            "text": "Where applicable law requires protections for international transfers of personal information, we will use legally recognized mechanisms or safeguards as appropriate."
          }
        ]
      },
      {
        "title": "16. Children",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS is an enterprise service and is not directed to children. We do not knowingly seek to collect personal information directly from children under 13 through the ordinary use of OASYS."
          },
          {
            "type": "p",
            "text": "If you believe a child has provided personal information directly to OASYS inappropriately, contact legal@oasyshq.com."
          }
        ]
      },
      {
        "title": "17. Third-Party Services and Links",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS may contain links to or integrations with third-party services. Those services are governed by their own terms and privacy practices. OASYS is not responsible for the independent privacy practices of third parties."
          },
          {
            "type": "p",
            "text": "Customers should review the privacy and security practices of systems they choose to connect to OASYS."
          }
        ]
      },
      {
        "title": "18. Changes to this Privacy Policy",
        "blocks": [
          {
            "type": "p",
            "text": "We may update this Privacy Policy from time to time to reflect changes in the Service, our information-processing practices, legal requirements, or security and operational practices."
          },
          {
            "type": "p",
            "text": "When we materially update the policy, we will update the effective date or version and provide additional notice when required by law or contract."
          },
          {
            "type": "p",
            "text": "The version published through the official OASYS legal surface is the current version unless otherwise stated."
          }
        ]
      },
      {
        "title": "19. Contact Us",
        "blocks": [
          {
            "type": "contact",
            "lines": [
              "Budd Hub LLC d/b/a OASYS Solutions",
              "Attn: Legal & Privacy",
              "7533 Center View Court, Suite N",
              "West Jordan, Utah 84084",
              "United States",
              "Email: legal@oasyshq.com"
            ]
          },
          {
            "type": "p",
            "text": "Security vulnerabilities or security-related reports should instead be sent to security@oasyshq.com."
          }
        ]
      }
    ]
  },
  "terms": {
    "key": "terms",
    "routeMode": "terms",
    "title": "OASYS Customer Terms",
    "shortTitle": "Customer Terms",
    "eyebrow": "OASYS Legal",
    "description": "The baseline terms governing customer access to and use of the OASYS software-as-a-service platform and related services.",
    "effectiveDate": "September 1, 2026",
    "lastUpdated": "September 1, 2026",
    "version": "1.0",
    "intro": [
      "These OASYS Customer Terms (“Customer Terms”) govern access to and use of the OASYS software-as-a-service platform and related services.",
      "These Customer Terms are between Budd Hub LLC, doing business as OASYS Solutions (“OASYS,” “we,” “us,” or “our”) and the organization or other legal entity obtaining access to the Service (“Customer”).",
      "If an individual accepts these Customer Terms or uses the Service on behalf of an organization, that individual represents that they have authority to bind that organization to these Customer Terms."
    ],
    "sections": [
      {
        "title": "1. Definitions",
        "blocks": [
          {
            "type": "p",
            "text": "“Authorized User” means an individual whom Customer authorizes to access the Service under Customer’s account."
          },
          {
            "type": "p",
            "text": "“Customer Data” means information, records, files, content, configurations, evidence, metadata, or other data submitted to, connected to, generated within, or otherwise made available to OASYS by or on behalf of Customer, excluding OASYS technology and Service-generated operational information that does not constitute Customer Data."
          },
          {
            "type": "p",
            "text": "“Documentation” means OASYS-provided documentation, instructions, guides, and other materials relating to use of the Service."
          },
          {
            "type": "p",
            "text": "“Order Form” means an ordering document, proposal, statement of work, subscription document, or similar commercial document accepted by Customer and OASYS."
          },
          {
            "type": "p",
            "text": "“Service” means the OASYS software-as-a-service platform, associated applications, hosted functionality, OASYS Intelligence capabilities, connectors, Documentation, support functions, and related services made available by OASYS."
          },
          {
            "type": "p",
            "text": "“Subscription Term” means the period during which Customer is authorized to use the Service under an applicable Order Form or agreement."
          }
        ]
      },
      {
        "title": "2. Agreement Structure and Order of Precedence",
        "blocks": [
          {
            "type": "p",
            "text": "These Customer Terms may be incorporated into an Order Form or other agreement between OASYS and Customer."
          },
          {
            "type": "p",
            "text": "If Customer and OASYS enter into a separately executed master services agreement, enterprise agreement, data processing agreement, Order Form, or other written agreement that expressly governs the Service, that agreement will control to the extent of any conflict with these Customer Terms."
          },
          {
            "type": "p",
            "text": "Unless otherwise expressly agreed in writing, the following order of precedence applies:"
          },
          {
            "type": "ol",
            "items": [
              "a separately executed negotiated agreement between OASYS and Customer;",
              "the applicable Order Form;",
              "any applicable data processing agreement;",
              "these Customer Terms; and",
              "Documentation."
            ]
          },
          {
            "type": "p",
            "text": "Nothing in these Customer Terms prevents OASYS and Customer from agreeing to different or additional terms in writing."
          }
        ]
      },
      {
        "title": "3. Access to the Service",
        "blocks": [
          {
            "type": "p",
            "text": "Subject to Customer’s compliance with these Customer Terms and any applicable Order Form, OASYS grants Customer, during the applicable Subscription Term, a limited, non-exclusive, non-transferable, non-sublicensable right to permit its Authorized Users to access and use the Service for Customer’s internal business purposes."
          },
          {
            "type": "p",
            "text": "The Service is provided as a hosted service. Except where expressly permitted in writing, Customer receives no right to obtain or possess source code or to install the OASYS software independently."
          },
          {
            "type": "p",
            "text": "No ownership interest in the Service or underlying intellectual property is transferred to Customer."
          }
        ]
      },
      {
        "title": "4. Customer Accounts and Authorized Users",
        "blocks": [
          {
            "type": "p",
            "text": "Customer is responsible for designating appropriate Authorized Users, ensuring Authorized Users comply with these Customer Terms, maintaining accurate account and organization information, assigning roles and permissions appropriately, protecting account access, promptly removing access for users who are no longer authorized, and notifying OASYS of suspected unauthorized account activity."
          },
          {
            "type": "p",
            "text": "Customer is responsible for activity occurring through its accounts and Authorized Users except to the extent caused by OASYS’s breach of its obligations."
          },
          {
            "type": "p",
            "text": "Authorized Users may not share credentials or allow unauthorized persons to use their accounts."
          }
        ]
      },
      {
        "title": "5. Customer Responsibilities",
        "blocks": [
          {
            "type": "p",
            "text": "Customer is responsible for determining whether the Service is appropriate for its business and compliance objectives."
          },
          {
            "type": "p",
            "text": "Customer will provide accurate information reasonably necessary to operate the Service; ensure that it has appropriate rights and authority to provide Customer Data to OASYS; obtain permissions or consents required for connected systems; configure its own systems and accounts appropriately; maintain appropriate administrative and security practices; review OASYS findings, recommendations, reports, and AI-assisted outputs using qualified personnel; and remain responsible for decisions affecting Customer’s systems, compliance program, security posture, and regulatory obligations."
          },
          {
            "type": "p",
            "text": "OASYS does not replace Customer’s legal, compliance, security, accounting, audit, or other professional advisers."
          }
        ]
      },
      {
        "title": "6. Acceptable Use",
        "blocks": [
          {
            "type": "p",
            "text": "Customer and its Authorized Users may not:"
          },
          {
            "type": "ul",
            "items": [
              "access or use the Service unlawfully;",
              "attempt to gain unauthorized access to another customer’s environment;",
              "circumvent authentication, authorization, tenant-isolation, usage, or security controls;",
              "probe, scan, or test vulnerabilities except through an expressly authorized security-testing program;",
              "introduce malware, malicious code, or harmful content;",
              "use the Service to infringe intellectual-property, privacy, or other rights;",
              "knowingly submit information Customer is not authorized to process;",
              "interfere with or disrupt the Service or its infrastructure;",
              "use automated means to overload or materially degrade the Service;",
              "reverse engineer, decompile, disassemble, or attempt to derive source code except to the limited extent such restriction is prohibited by applicable law;",
              "copy, resell, sublicense, distribute, or provide the Service to third parties except as expressly authorized;",
              "remove proprietary notices;",
              "use OASYS technology to build or train a directly competing product through unauthorized extraction or systematic replication of proprietary functionality; or",
              "use the Service for unlawful surveillance, discrimination, fraud, or other unlawful purposes."
            ]
          },
          {
            "type": "p",
            "text": "OASYS may investigate suspected misuse and may restrict access when reasonably necessary to protect the Service, customers, or others."
          }
        ]
      },
      {
        "title": "7. Customer Data",
        "blocks": [
          {
            "type": "p",
            "text": "As between OASYS and Customer, Customer retains its rights in Customer Data."
          },
          {
            "type": "p",
            "text": "Customer grants OASYS a limited right to host, copy, transmit, process, display, analyze, and otherwise use Customer Data only as reasonably necessary to provide and operate the Service, perform Customer-requested functionality, maintain and secure the Service, provide support, comply with Customer instructions, comply with applicable law, enforce these Customer Terms, and protect the Service."
          },
          {
            "type": "p",
            "text": "This authorization continues only for as long as reasonably necessary for those purposes, subject to applicable retention, backup, audit, legal, and contractual requirements."
          },
          {
            "type": "p",
            "text": "OASYS does not acquire ownership of Customer Data merely because it is processed through the Service."
          }
        ]
      },
      {
        "title": "8. Privacy and Data Processing",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS processes personal information in accordance with its applicable Privacy Policy and contractual obligations."
          },
          {
            "type": "p",
            "text": "Where OASYS processes personal information on Customer’s behalf, the parties may enter into a data processing agreement when required by applicable law or the parties’ agreement."
          },
          {
            "type": "p",
            "text": "Customer is responsible for determining whether Customer’s use of the Service requires notices, consents, contractual terms, assessments, or other privacy measures applicable to Customer."
          }
        ]
      },
      {
        "title": "9. Security",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS maintains administrative, technical, and organizational safeguards designed to protect the Service and information processed through it. OASYS security practices may include controls relating to authentication, authorization, tenant isolation, production access, encryption, logging, auditability, secrets management, monitoring, deployment controls, and incident response."
          },
          {
            "type": "p",
            "text": "No system can guarantee absolute security. Customer remains responsible for security controls within systems, accounts, networks, applications, credentials, and infrastructure under Customer’s control."
          },
          {
            "type": "p",
            "text": "Security concerns may be reported to security@oasyshq.com."
          }
        ]
      },
      {
        "title": "10. Connectors and Third-Party Systems",
        "blocks": [
          {
            "type": "p",
            "text": "Customer may choose to connect supported third-party systems to OASYS. Customer is solely responsible for deciding which systems to connect, which accounts or resources to authorize, whether Customer has authority to make the connection, and which permissions or scopes to grant."
          },
          {
            "type": "p",
            "text": "OASYS connectors are designed around customer-authorized, read-only evidence collection unless OASYS and Customer expressly agree otherwise in writing."
          },
          {
            "type": "p",
            "text": "Durable connector authorization material is encrypted before storage and decrypted only when required for authorized execution."
          },
          {
            "type": "p",
            "text": "OASYS does not independently authorize itself to access Customer systems. Customer may be required to complete authentication, authorization, consent, or installation procedures required by a third-party provider."
          },
          {
            "type": "p",
            "text": "Third-party systems are governed by their own agreements and privacy practices. OASYS is not responsible for the availability, operation, modification, discontinuation, or security of third-party systems outside OASYS’s control."
          }
        ]
      },
      {
        "title": "11. OASYS Intelligence and AI-Assisted Functionality",
        "blocks": [
          {
            "type": "p",
            "text": "The Service may include artificial intelligence or machine-learning-assisted capabilities, including OASYS Intelligence. These capabilities may provide summaries, explanations, recommendations, prioritization, contextual guidance, decision-support information, and other advisory outputs."
          },
          {
            "type": "p",
            "text": "OASYS Intelligence is designed to use governed OASYS context. AI-assisted outputs are advisory."
          },
          {
            "type": "p",
            "text": "Deterministic OASYS records, calculations, authorization state, validation engines, workflow state, evidence records, and other governed application state remain authoritative."
          },
          {
            "type": "p",
            "text": "AI-assisted functionality does not independently grant or expand permissions, override access controls, approve risk, authorize connectors, execute destructive remediation, validate a remediation, approve an exception, close a task, certify compliance, or override human or deterministic governance controls."
          },
          {
            "type": "p",
            "text": "Customer is responsible for reviewing AI-assisted outputs before relying on them. AI-generated information may contain errors, omissions, or incomplete reasoning and should not be treated as professional legal, accounting, security, audit, or regulatory advice."
          }
        ]
      },
      {
        "title": "12. Compliance Platform Disclaimer",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS provides software intended to assist organizations with compliance operations, evidence, governance, visibility, remediation workflows, validation, reporting, and related activities."
          },
          {
            "type": "p",
            "text": "OASYS may support controls, mappings, workflows, or functionality associated with regulatory or industry frameworks."
          },
          {
            "type": "p",
            "text": "Framework support does not constitute certification, accreditation, legal compliance, regulatory approval, or an assurance that Customer satisfies the requirements of any framework or law."
          },
          {
            "type": "p",
            "text": "Use of OASYS does not guarantee successful certification, successful audit results, regulatory compliance, absence of security incidents, absence of findings, or acceptance by an auditor or regulator."
          },
          {
            "type": "p",
            "text": "Customer remains responsible for determining and satisfying its legal, contractual, regulatory, security, and compliance obligations."
          }
        ]
      },
      {
        "title": "13. Reports, Evidence, and Audit Materials",
        "blocks": [
          {
            "type": "p",
            "text": "The Service may generate or organize reports, evidence packages, audit records, summaries, exports, and other materials based on information available to OASYS."
          },
          {
            "type": "p",
            "text": "Customer is responsible for reviewing such materials for accuracy and appropriateness before providing them to an auditor, regulator, customer, business partner, or other third party."
          },
          {
            "type": "p",
            "text": "OASYS does not represent that a generated report or evidence package will satisfy the requirements of a particular auditor, regulator, certification body, or third party."
          }
        ]
      },
      {
        "title": "14. Fees and Payment",
        "blocks": [
          {
            "type": "p",
            "text": "Customer will pay the fees specified in the applicable Order Form or other commercial agreement."
          },
          {
            "type": "p",
            "text": "Unless otherwise stated in writing, fees are stated in U.S. dollars; Customer is responsible for applicable taxes other than taxes imposed on OASYS’s net income; payment obligations are non-cancelable during the applicable committed Subscription Term; and fees paid are non-refundable except where expressly provided by the applicable agreement."
          },
          {
            "type": "p",
            "text": "Specific invoicing, renewal, payment timing, usage limits, and pricing terms may be established in the applicable Order Form. If Customer disputes an invoice in good faith, Customer should notify OASYS promptly at billing@oasyshq.com."
          }
        ]
      },
      {
        "title": "15. Changes to the Service",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS may modify, improve, update, or replace features of the Service over time and may discontinue features when reasonably necessary for security, technical, legal, provider, or product reasons."
          },
          {
            "type": "p",
            "text": "We will use commercially reasonable efforts to avoid materially reducing core paid functionality during a Subscription Term without appropriate notice, subject to emergencies, legal requirements, third-party changes, security concerns, or circumstances outside OASYS’s reasonable control."
          },
          {
            "type": "p",
            "text": "Nothing in these Customer Terms requires OASYS to provide any particular future feature unless expressly committed in a signed agreement."
          }
        ]
      },
      {
        "title": "16. Support",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS will provide support according to the applicable plan, Order Form, Documentation, or separately agreed support terms."
          },
          {
            "type": "p",
            "text": "General support requests may be directed to support@oasyshq.com. OASYS does not guarantee a particular response or resolution time unless expressly stated in an applicable service-level or support agreement."
          }
        ]
      },
      {
        "title": "17. Confidentiality",
        "blocks": [
          {
            "type": "p",
            "text": "Each party may receive non-public information from the other that is identified as confidential or that reasonably should be understood to be confidential given the nature of the information and circumstances of disclosure (“Confidential Information”)."
          },
          {
            "type": "p",
            "text": "Confidential Information may include Customer Data, security information, business plans, pricing, technical information, product designs, non-public Documentation, system architecture, credentials, proprietary methods, and commercial information."
          },
          {
            "type": "p",
            "text": "The receiving party will use Confidential Information only as necessary to perform or exercise rights under the parties’ agreement; protect it using reasonable care; and disclose it only to personnel, contractors, advisers, and service providers who need the information and are subject to appropriate confidentiality obligations."
          },
          {
            "type": "p",
            "text": "Confidential Information does not include information that the receiving party can demonstrate was lawfully known without confidentiality restriction, becomes public through no breach of obligation, is lawfully received from another source without confidentiality restriction, or is independently developed without use of the other party’s Confidential Information."
          },
          {
            "type": "p",
            "text": "A party may disclose Confidential Information if legally required, provided it gives notice where legally permitted and reasonably cooperates with lawful protective efforts."
          }
        ]
      },
      {
        "title": "18. Intellectual Property",
        "blocks": [
          {
            "type": "p",
            "text": "The Service, software, technology, architecture, interfaces, workflows, designs, Documentation, models, methods, visual elements, trademarks, and related intellectual property are owned by OASYS and/or its licensors."
          },
          {
            "type": "p",
            "text": "Certain OASYS software and intellectual property are licensed to the operating company by its licensors, including intellectual property owned by Nebularis Holdings LLC."
          },
          {
            "type": "p",
            "text": "Except for the limited access rights expressly granted under these Customer Terms or an applicable written agreement, no intellectual-property rights are transferred to Customer."
          },
          {
            "type": "p",
            "text": "Customer may not use OASYS trademarks, logos, or branding except as expressly authorized."
          }
        ]
      },
      {
        "title": "19. Feedback",
        "blocks": [
          {
            "type": "p",
            "text": "If Customer or an Authorized User voluntarily provides suggestions, ideas, feature requests, or other feedback relating to the Service (“Feedback”), Customer grants OASYS a perpetual, irrevocable, worldwide, royalty-free right to use and incorporate that Feedback into OASYS products and services without restriction or payment."
          },
          {
            "type": "p",
            "text": "This does not grant OASYS ownership of Customer Data or Customer Confidential Information."
          }
        ]
      },
      {
        "title": "20. Third-Party Technology",
        "blocks": [
          {
            "type": "p",
            "text": "The Service may depend on or interoperate with third-party technology, infrastructure, software, APIs, models, and services. Third-party components may be subject to separate license terms."
          },
          {
            "type": "p",
            "text": "OASYS is not responsible for failures caused solely by third-party services outside OASYS’s reasonable control, but we will use commercially reasonable efforts to operate and support the Service consistent with our applicable obligations."
          }
        ]
      },
      {
        "title": "21. Suspension",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS may suspend or restrict Customer access when reasonably necessary because of material breach of these Customer Terms or an applicable agreement; nonpayment where permitted by the applicable agreement; actual or suspected unauthorized access; security threats; unlawful use; material risk to OASYS, Customer, another customer, or a third party; legal or governmental requirements; or misuse that threatens the availability or integrity of the Service."
          },
          {
            "type": "p",
            "text": "Where practical and appropriate, OASYS will provide notice and an opportunity to cure before suspension. OASYS will use reasonable efforts to limit a suspension to the affected users, systems, or functionality where appropriate."
          }
        ]
      },
      {
        "title": "22. Term and Termination",
        "blocks": [
          {
            "type": "p",
            "text": "These Customer Terms remain in effect while Customer is authorized to use the Service. Subscription duration, renewal, and termination rights may be established in an applicable Order Form or negotiated agreement."
          },
          {
            "type": "p",
            "text": "Either party may terminate for material breach if the other party fails to cure the breach within an applicable contractual cure period. OASYS may terminate access immediately where continued access would be unlawful or present an unreasonable security risk."
          },
          {
            "type": "p",
            "text": "Expiration or termination does not automatically eliminate obligations that by their nature should survive."
          }
        ]
      },
      {
        "title": "23. Effect of Termination and Offboarding",
        "blocks": [
          {
            "type": "p",
            "text": "Upon expiration or termination, Customer’s right to access the Service ends except where temporary access is expressly provided for offboarding; OASYS may disable Authorized User access; Customer-authorized connector access may be disconnected or revoked; Customer may request available export or offboarding assistance according to the applicable agreement and OASYS processes; and retention and deletion processes will proceed according to applicable agreements, policies, legal requirements, security requirements, and backup lifecycle limitations."
          },
          {
            "type": "p",
            "text": "OASYS does not treat termination as authorization for silent, immediate destruction of all records."
          },
          {
            "type": "p",
            "text": "Certain records may be retained when reasonably required for security, auditability, fraud prevention, contractual obligations, legal obligations, dispute resolution, enforcement, or backup lifecycle requirements."
          },
          {
            "type": "p",
            "text": "More specific data-lifecycle provisions may be stated in an Order Form, data processing agreement, Documentation, or other OASYS policy."
          }
        ]
      },
      {
        "title": "24. Representations and Warranties",
        "blocks": [
          {
            "type": "p",
            "text": "Each party represents that it has the authority to enter into the applicable agreement."
          },
          {
            "type": "p",
            "text": "OASYS warrants that it will provide the Service in a professional manner substantially consistent with applicable Documentation and any express commitments in an applicable Order Form."
          },
          {
            "type": "p",
            "text": "If Customer believes OASYS has materially breached this warranty, Customer must notify OASYS and provide reasonable information necessary to investigate. Any additional warranties must be expressly stated in a written agreement signed by OASYS."
          }
        ]
      },
      {
        "title": "25. Disclaimers",
        "blocks": [
          {
            "type": "notice",
            "text": "EXCEPT AS EXPRESSLY PROVIDED IN AN APPLICABLE WRITTEN AGREEMENT AND TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.”"
          },
          {
            "type": "notice",
            "text": "OASYS DISCLAIMS IMPLIED WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE."
          },
          {
            "type": "p",
            "text": "OASYS does not warrant that the Service will be uninterrupted or error-free; every defect will be corrected; Customer will achieve any particular compliance or security result; AI-assisted outputs will always be accurate or complete; or the Service will satisfy every law, regulation, auditor, or customer requirement applicable to Customer."
          },
          {
            "type": "p",
            "text": "Some jurisdictions may not permit certain disclaimers, in which case those disclaimers apply only to the extent permitted by law."
          }
        ]
      },
      {
        "title": "26. Limitation of Liability",
        "blocks": [
          {
            "type": "notice",
            "text": "TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE TO THE OTHER FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR FOR LOST PROFITS, LOST REVENUE, LOST BUSINESS OPPORTUNITIES, OR LOSS OF GOODWILL, ARISING OUT OF OR RELATING TO THE SERVICE OR THE PARTIES’ AGREEMENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES."
          },
          {
            "type": "notice",
            "text": "UNLESS AN APPLICABLE ORDER FORM OR NEGOTIATED AGREEMENT PROVIDES OTHERWISE, EACH PARTY’S AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THE SERVICE OR THESE CUSTOMER TERMS WILL NOT EXCEED THE AMOUNTS PAID OR PAYABLE BY CUSTOMER TO OASYS FOR THE SERVICE DURING THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM."
          },
          {
            "type": "p",
            "text": "The limitations in this section will apply to the maximum extent permitted by law. A negotiated agreement may establish different limitations, exclusions, carve-outs, or caps."
          }
        ]
      },
      {
        "title": "27. Indemnification",
        "blocks": [
          {
            "type": "h3",
            "text": "Customer indemnity"
          },
          {
            "type": "p",
            "text": "Customer will defend and indemnify OASYS and its affiliates, officers, directors, employees, and licensors against third-party claims arising from Customer Data that infringes or violates third-party rights; Customer’s unlawful use of the Service; Customer’s material breach of the acceptable-use provisions; or Customer’s unauthorized connection to or use of a third-party system."
          },
          {
            "type": "h3",
            "text": "OASYS intellectual-property indemnity"
          },
          {
            "type": "p",
            "text": "Unless superseded by an applicable negotiated agreement, OASYS will defend Customer against a third-party claim alleging that Customer’s authorized use of the Service infringes a United States patent, copyright, or trademark, and will indemnify Customer for damages finally awarded or settlements approved by OASYS arising from that claim."
          },
          {
            "type": "p",
            "text": "OASYS will have no obligation to the extent a claim results from Customer Data; unauthorized modification; combination with products or services not supplied or approved by OASYS where the claim would not otherwise exist; use contrary to Documentation or agreement; or continued use after OASYS provides a non-infringing alternative."
          },
          {
            "type": "p",
            "text": "If the Service becomes subject to an infringement claim, OASYS may obtain the right for Customer to continue using the affected functionality; modify or replace it with substantially equivalent functionality; or, if neither is commercially reasonable, terminate the affected functionality and refund prepaid fees attributable to the unused portion of the affected Subscription Term."
          },
          {
            "type": "p",
            "text": "Indemnification procedures may be further defined in an applicable negotiated agreement."
          }
        ]
      },
      {
        "title": "28. Publicity",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS will not use Customer’s name or logo in public marketing materials without Customer’s permission unless expressly authorized in an Order Form or other written agreement."
          },
          {
            "type": "p",
            "text": "This restriction does not prevent OASYS from identifying Customer where required by law or as reasonably necessary for private operational purposes."
          }
        ]
      },
      {
        "title": "29. Compliance with Laws",
        "blocks": [
          {
            "type": "p",
            "text": "Each party will comply with laws applicable to its own performance under the parties’ agreement."
          },
          {
            "type": "p",
            "text": "Customer is responsible for laws applicable to Customer’s business, Customer Data, Customer’s use of the Service, systems Customer connects to OASYS, and Customer’s compliance and regulatory obligations."
          },
          {
            "type": "p",
            "text": "OASYS is responsible for laws applicable to OASYS’s provision and operation of the Service."
          }
        ]
      },
      {
        "title": "30. Export Controls and Sanctions",
        "blocks": [
          {
            "type": "p",
            "text": "Customer may not use, export, re-export, or provide access to the Service in violation of applicable United States export-control or sanctions laws."
          },
          {
            "type": "p",
            "text": "Customer represents that it will not knowingly permit access to the Service by a person or entity prohibited from receiving the Service under applicable law."
          }
        ]
      },
      {
        "title": "31. Government Use",
        "blocks": [
          {
            "type": "p",
            "text": "If Customer is a governmental entity, additional terms may be required."
          },
          {
            "type": "p",
            "text": "The Service and Documentation are commercial products developed at private expense except as otherwise stated in an applicable written agreement."
          },
          {
            "type": "p",
            "text": "Nothing in these Customer Terms automatically waives governmental rights or requirements that cannot legally be waived."
          }
        ]
      },
      {
        "title": "32. Force Majeure",
        "blocks": [
          {
            "type": "p",
            "text": "Neither party will be liable for delay or failure to perform caused by circumstances beyond its reasonable control, including natural disasters, war, terrorism, civil unrest, widespread internet or telecommunications failures, labor disputes, governmental actions, third-party infrastructure failures, or other similar events."
          },
          {
            "type": "p",
            "text": "This section does not excuse Customer’s obligation to pay amounts already due for Service previously provided."
          }
        ]
      },
      {
        "title": "33. Assignment",
        "blocks": [
          {
            "type": "p",
            "text": "Neither party may assign the parties’ agreement without the other party’s prior written consent, except that either party may assign it in connection with a merger, acquisition, corporate reorganization, or sale of substantially all assets relating to the agreement, provided the assignee agrees to assume the assigning party’s obligations."
          },
          {
            "type": "p",
            "text": "Any prohibited assignment is void."
          }
        ]
      },
      {
        "title": "34. Notices",
        "blocks": [
          {
            "type": "p",
            "text": "Routine operational communications may be delivered electronically through the Service or to the email addresses associated with Customer’s account."
          },
          {
            "type": "p",
            "text": "Formal legal notices to OASYS should be sent to:"
          },
          {
            "type": "contact",
            "lines": [
              "Budd Hub LLC d/b/a OASYS Solutions",
              "Attn: Legal",
              "7533 Center View Court, Suite N",
              "West Jordan, Utah 84084",
              "United States",
              "Email: legal@oasyshq.com"
            ]
          },
          {
            "type": "p",
            "text": "Formal notices to Customer may be delivered to the administrative or legal contact identified in Customer’s account, Order Form, or applicable agreement. A negotiated agreement may establish additional notice requirements."
          }
        ]
      },
      {
        "title": "35. Governing Law and Venue",
        "blocks": [
          {
            "type": "p",
            "text": "Unless an applicable negotiated agreement provides otherwise, these Customer Terms and disputes arising from them are governed by the laws of the State of Utah, without regard to conflict-of-laws principles."
          },
          {
            "type": "p",
            "text": "The parties consent to exclusive jurisdiction and venue in the state or federal courts located in Salt Lake County, Utah, for disputes not otherwise governed by a separate dispute-resolution provision in an applicable agreement."
          },
          {
            "type": "p",
            "text": "Each party waives objections based on inconvenient forum to the extent permitted by law."
          }
        ]
      },
      {
        "title": "36. Changes to These Customer Terms",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS may update these Customer Terms from time to time. We will maintain a version and effective date."
          },
          {
            "type": "p",
            "text": "For material changes affecting an active Customer relationship, OASYS will provide notice when required by contract or applicable law."
          },
          {
            "type": "p",
            "text": "Unless required for legal or security reasons, changes to these online Customer Terms will not retroactively alter a separately signed agreement or Order Form."
          },
          {
            "type": "p",
            "text": "The version incorporated into an applicable Order Form or otherwise accepted by Customer governs unless the parties agree otherwise."
          }
        ]
      },
      {
        "title": "37. Electronic Communications",
        "blocks": [
          {
            "type": "p",
            "text": "Customer agrees that OASYS may provide routine Service-related communications electronically."
          },
          {
            "type": "p",
            "text": "Where electronic acceptance is used to establish contractual consent, OASYS may maintain records of the accepting organization, accepting user, accepted document, document version, acceptance date and time, and acceptance method."
          },
          {
            "type": "p",
            "text": "The parties may also execute agreements electronically."
          }
        ]
      },
      {
        "title": "38. General Provisions",
        "blocks": [
          {
            "type": "p",
            "text": "If any provision of these Customer Terms is held unenforceable, the remaining provisions remain effective, and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable where permitted."
          },
          {
            "type": "p",
            "text": "Failure to enforce a provision is not a waiver. Headings are for convenience only."
          },
          {
            "type": "p",
            "text": "These Customer Terms, together with the applicable Order Form and any documents expressly incorporated by reference, constitute the agreement concerning their subject matter unless superseded by another written agreement."
          },
          {
            "type": "p",
            "text": "No third party is a beneficiary of these Customer Terms except where expressly stated."
          }
        ]
      },
      {
        "title": "39. Contact",
        "blocks": [
          {
            "type": "contact",
            "lines": [
              "Budd Hub LLC d/b/a OASYS Solutions",
              "Attn: Legal",
              "7533 Center View Court, Suite N",
              "West Jordan, Utah 84084",
              "United States",
              "Legal: legal@oasyshq.com",
              "Support: support@oasyshq.com",
              "Billing: billing@oasyshq.com",
              "Security: security@oasyshq.com"
            ]
          }
        ]
      }
    ]

  },
  "lifecycle": {
    "key": "lifecycle",
    "routeMode": "lifecycle",
    "title": "OASYS Data Lifecycle Policy",
    "shortTitle": "Data Lifecycle",
    "eyebrow": "OASYS Policy",
    "description": "How OASYS governs retention, lifecycle holds, offboarding, deletion, governed archives, and backup lifecycle for Customer Data.",
    "effectiveDate": "September 5, 2026",
    "lastUpdated": "September 5, 2026",
    "version": "1.0",
    "intro": [
      "This OASYS Data Lifecycle Policy describes the standard lifecycle controls that OASYS applies to Customer Data and related lifecycle records processed through the Service.",
      "This policy supplements the OASYS Privacy Policy and OASYS Customer Terms. If a signed Order Form, data processing agreement, negotiated addendum, or other written agreement establishes different lifecycle requirements, the signed agreement controls to the extent of the conflict.",
      "OASYS does not apply one universal retention period to every category of information. Retention and deletion depend on the data category, lifecycle state, applicable holds, customer-specific requirements, legal or security obligations, storage layer, and whether information has been intentionally placed into governed archive."
    ],
    "sections": [
      {
        "title": "1. Scope and Lifecycle Categories",
        "blocks": [
          {
            "type": "p",
            "text": "For purposes of this policy, Customer Data includes information, records, files, content, configurations, evidence, metadata, and related data submitted to, connected to, generated within, or otherwise made available to OASYS by or on behalf of a Customer, subject to the definitions and exclusions in the applicable Customer Terms."
          },
          {
            "type": "p",
            "text": "OASYS distinguishes among ordinary active tenant data, AI interaction content, storage-layer object versions, governed archive records and objects, lifecycle audit records, and backup or recovery copies. These categories can have different lifecycle behavior."
          },
          {
            "type": "p",
            "text": "A retention period stated in this policy describes the standard production policy for the identified category. It does not override a valid lifecycle hold, a longer period required by law or contract, or a more specific written Customer agreement."
          }
        ]
      },
      {
        "title": "2. Lifecycle Authority and Holds",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS maintains lifecycle request, hold, and execution records so material lifecycle actions can be authorized, scoped, and reviewed. Lifecycle requests may address activities such as export, offboarding, deletion, or retention overrides."
          },
          {
            "type": "p",
            "text": "An active legal, security, regulatory, contractual, or other authorized lifecycle hold takes precedence over ordinary deletion for the data scope covered by that hold. Holds may apply to an entire tenant or to specific categories such as operational data, AI interactions, evidence, audit records, governed archive, backups, or connector authorization records."
          },
          {
            "type": "p",
            "text": "Governed archive and backup data follow their own protected lifecycle. A hold or retention requirement limited to those protected categories does not require OASYS to delete those categories through the ordinary tenant purge process."
          }
        ]
      },
      {
        "title": "3. AI Interaction Retention",
        "blocks": [
          {
            "type": "p",
            "text": "The standard OASYS production retention threshold for AI interaction content is 90 days. AI interaction records older than the applicable threshold become eligible for governed deletion."
          },
          {
            "type": "p",
            "text": "AI interaction deletion is tenant-scoped, hold-aware, bounded, and recorded in the lifecycle execution ledger. An active tenant-level or AI-interaction hold blocks deletion of the affected records."
          },
          {
            "type": "notice",
            "text": "The 90-day period is an eligibility threshold, not a promise that every record will be physically deleted at the exact moment it reaches 90 days. Deletion may occur later because of an applicable hold, safety control, incident or legal requirement, or governed operational processing."
          }
        ]
      },
      {
        "title": "4. Ordinary Versioned Object Storage",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS uses versioned object storage for ordinary evidence and SOP-related artifacts. Storage-layer noncurrent object versions are treated differently from the current object and from logical records maintained in OASYS."
          },
          {
            "type": "ul",
            "items": [
              "Noncurrent ordinary evidence object versions in the governed tenant evidence scope are configured to age out after 90 days.",
              "Noncurrent ordinary SOP-storage object versions are configured to age out after 30 days.",
              "The current object version is not deleted by these noncurrent-version lifecycle rules."
            ]
          },
          {
            "type": "p",
            "text": "These storage-layer windows do not mean that the current evidence record, current SOP document, or logical SOP history is automatically deleted after 30 or 90 days."
          },
          {
            "type": "p",
            "text": "OASYS does not use broad prefix deletion for unattributed or legacy ordinary objects during tenant offboarding. An object that cannot be safely attributed to the offboarding tenant is preserved for review rather than swept solely because it shares a storage prefix."
          }
        ]
      },
      {
        "title": "5. Governed Archive",
        "blocks": [
          {
            "type": "p",
            "text": "When an artifact is intentionally placed into the OASYS governed archive, OASYS records tenant ownership, the retention period, the retain-until time, and AWS-observed Object Lock information before treating the artifact as archived."
          },
          {
            "type": "p",
            "text": "The standard governed-archive retention default is seven years unless a different valid retention period is explicitly selected for the archived artifact."
          },
          {
            "type": "p",
            "text": "Governed archive objects use Amazon S3 Object Lock in GOVERNANCE mode. Ordinary OASYS runtime authority does not delete governed archive objects or request governance-retention bypass."
          },
          {
            "type": "notice",
            "text": "AWS Object Lock GOVERNANCE mode is not the same as AWS Object Lock COMPLIANCE mode. A specifically authorized AWS administrator with applicable bypass authority may be able to override Governance-mode retention where AWS permits it. OASYS therefore does not describe Governance-mode archive storage as absolutely irreversible."
          },
          {
            "type": "p",
            "text": "Governed archive records and governed archive objects are excluded from ordinary tenant offboarding purge and remain subject to their recorded retention requirements."
          }
        ]
      },
      {
        "title": "6. Offboarding and Access Quiescence",
        "blocks": [
          {
            "type": "p",
            "text": "Customer offboarding is a governed lifecycle process rather than a single tenant-row deletion. Destructive offboarding requires an authorized offboarding request, an in-progress lifecycle barrier, completion of required quiescence checks, applicable hold evaluation, and explicit execution authority."
          },
          {
            "type": "p",
            "text": "During quiescence, OASYS can suspend active tenant memberships, cancel pending invitations, move queued synchronization work to a terminal state, require already-running synchronization work to finish, and disconnect locally stored enterprise connector authorization material before ordinary purge proceeds."
          },
          {
            "type": "p",
            "text": "Disconnecting OASYS-stored connector authorization material does not by itself prove that a third-party provider has revoked authorization on the provider side. Provider-side revocation may require separate Customer or provider action."
          },
          {
            "type": "p",
            "text": "Offboarding one OASYS tenant does not necessarily delete a user's global identity record where that identity may be used outside the offboarded tenant. OASYS does not represent tenant offboarding as global identity-provider account deletion."
          }
        ]
      },
      {
        "title": "7. Ordinary Tenant Purge",
        "blocks": [
          {
            "type": "p",
            "text": "After quiescence is complete and no blocking lifecycle hold remains, OASYS may execute an explicitly authorized purge of ordinary tenant-scoped data using a frozen, governed tenant-data allowlist and dependency-aware deletion order. The tenant root is deleted only after tenant-scoped child data is processed."
          },
          {
            "type": "p",
            "text": "For ordinary S3 objects discovered through the offboarding plan, OASYS revalidates exact tenant ownership and the exact S3 VersionId before deletion. The purge process deletes the verified VersionId rather than issuing an unversioned key-only delete."
          },
          {
            "type": "p",
            "text": "Lifecycle hold records, lifecycle request records, lifecycle execution records, and governed archive registry records are protected lifecycle survivors and are not deleted as ordinary tenant data."
          },
          {
            "type": "p",
            "text": "Completion is separate from destructive execution. Before an offboarding request is marked completed, OASYS independently verifies that the governed ordinary database scope is terminal and that each exact ordinary S3 VersionId recorded as deleted is absent."
          }
        ]
      },
      {
        "title": "8. Export and Customer Retrieval Before Purge",
        "blocks": [
          {
            "type": "p",
            "text": "Customers that require an export or retrieval of available Customer Data should request it before destructive offboarding purge is authorized."
          },
          {
            "type": "p",
            "text": "Export availability, format, scope, assistance, and timing depend on the capabilities available in the Service and any applicable Customer agreement. This policy does not create a universal export format, export deadline, or separate export service-level commitment."
          },
          {
            "type": "p",
            "text": "After ordinary tenant data has been successfully purged, OASYS does not promise that the deleted active-service data can be reconstructed for general Customer access from protected archives or recovery copies."
          }
        ]
      },
      {
        "title": "9. Backups and Recovery Copies",
        "blocks": [
          {
            "type": "p",
            "text": "Backup and recovery copies are separate from the ordinary active-service purge transaction. Information deleted from active tenant storage may remain in a backup or recovery copy until that copy expires, is replaced, or is otherwise removed through the applicable backup lifecycle."
          },
          {
            "type": "p",
            "text": "Backup retention may also be extended where required by an applicable legal, security, regulatory, contractual, or authorized lifecycle hold."
          },
          {
            "type": "p",
            "text": "Unless a signed Customer agreement states otherwise, OASYS does not make one fixed customer-facing backup-retention window a universal contractual promise because recovery architecture and provider retention capabilities may change over time."
          }
        ]
      },
      {
        "title": "10. Completion Records and Auditability",
        "blocks": [
          {
            "type": "p",
            "text": "When governed offboarding completion succeeds, OASYS records completion verification in the lifecycle ledger and marks the applicable lifecycle request completed."
          },
          {
            "type": "p",
            "text": "Lifecycle request and execution records are intentionally retained after ordinary tenant data purge so OASYS can preserve evidence of authorization, holds, execution scope, verification results, and completion status."
          },
          {
            "type": "p",
            "text": "Completion records do not represent that OASYS globally deleted an identity-provider account or independently completed provider-side authorization revocation unless that separate action is specifically performed and evidenced."
          }
        ]
      },
      {
        "title": "11. Customer-Specific and Legal Requirements",
        "blocks": [
          {
            "type": "p",
            "text": "A Customer may have legal, regulatory, contractual, evidentiary, or business requirements that differ from OASYS standard lifecycle settings. Customer is responsible for identifying requirements applicable to Customer and for communicating any agreed lifecycle requirements through the applicable contracting, support, legal, or governance process."
          },
          {
            "type": "p",
            "text": "OASYS may retain information longer than an ordinary lifecycle period where reasonably necessary to comply with law, legal process, security obligations, dispute or enforcement requirements, contractual obligations, or an authorized lifecycle hold."
          },
          {
            "type": "p",
            "text": "Where OASYS processes Customer Data on behalf of a Customer, individual privacy requests concerning that Customer Data may need to be directed to or coordinated with the Customer as described in the OASYS Privacy Policy."
          }
        ]
      },
      {
        "title": "12. Policy Changes and Contact",
        "blocks": [
          {
            "type": "p",
            "text": "OASYS may update this Data Lifecycle Policy as the Service, storage architecture, legal requirements, or lifecycle controls evolve. OASYS will maintain a version, effective date, and last-updated date for the published policy."
          },
          {
            "type": "p",
            "text": "A separately signed agreement is not retroactively changed by an online policy update unless the signed agreement provides otherwise."
          },
          {
            "type": "contact",
            "lines": [
              "Budd Hub LLC d/b/a OASYS Solutions",
              "Attn: Legal",
              "7533 Center View Court, Suite N",
              "West Jordan, Utah 84084",
              "United States",
              "Legal: legal@oasyshq.com",
              "Support: support@oasyshq.com",
              "Security: security@oasyshq.com"
            ]
          }
        ]
      }
    ]
  }
};

export function getLegalDocument(documentKey) {
  const key = String(documentKey || "").trim().toLowerCase();
  return LEGAL_DOCUMENTS[key] || null;
}
